import React, { useEffect } from 'react'

/**
 * ONBOARDINGS MODULE DESIGN SYSTEM — extracted from Natalie's prototype
 * mockups (03_Prototype_mockups/source/*.dc.html in the handover zip).
 * The module pages are styled with THESE primitives (inline styles, exact
 * hexes) rather than the dashboard's Tailwind theme, so they match the
 * signed-off design pixel-close. Rebuild after Lee's 30 Oct review:
 * "most of it looks nothing like the design".
 */

/* ── colour tokens (verbatim from the mockups) ── */
export const OB = {
  pageBg: '#F4F6F9', surface: '#FFFFFF', ink: '#1B2433', ink2: '#3A4555',
  muted: '#5B6676', faint: '#8A94A3', faint2: '#9AA5B4',
  border: '#E1E6EE', rowDiv: '#EEF1F5', inputBorder: '#C9D3E1',
  accent: '#1D4ED8', accentSoft: '#DCE8FD', accentPale: '#EAF1FE', accentBorder: '#BFD3FA', accentMid: '#9DB8F2',
  track: '#F1F4F8', headerBg: '#F7F9FC',
  green: '#146C3A', greenBg: '#E3F4EA', greenDot: '#1F9D57', greenBorder: '#A9DDBD',
  red: '#B42318', redBg: '#FDE3E1', redPale: '#FDF1F0', redBorder: '#F3B8B2',
  amber: '#8F5200', amberDark: '#7A4600', amberInk: '#4A2E00', amberBg: '#FDEBC8', amberPale: '#FFF4DE', amberBorder: '#EBB55A', amberDot: '#D98A12', amberRing: '#C77700',
  purple: '#7A2F87', purpleBg: '#F0E6F4',
  lockBorder: '#D5DCE5',
  font: "'Nunito Sans', 'Mulish', 'Segoe UI', system-ui, sans-serif",
} as const

/** Pathway colours (CoSessions/CoPathways legend). */
export const PATHWAY_COLORS: Record<string, string> = {
  'Training & Setup': '#1D4ED8',
  'Data migration': '#8f4399',
  'Proposal set-up': '#0c8279',
  Account: '#5B6676',
}

/** Person avatar colours (all mockups). Customers → #2F4A6B, unknown → #6B7686. */
const PEOPLE: Record<string, string> = { natalie: '#0a5f9e', lee: '#8f4399', andy: '#c0392b', jennie: '#0c8279', john: '#8F5200' }
export const personColor = (name?: string | null, customer = false): string => {
  if (!name) return '#6B7686'
  const first = name.trim().split(/\s+/)[0].toLowerCase()
  return PEOPLE[first] || (customer ? '#2F4A6B' : '#6B7686')
}

/** Load Nunito Sans once (the mockups' font). Falls back to Mulish. */
let fontLoaded = false
export const useObFont = () => {
  useEffect(() => {
    if (fontLoaded || document.getElementById('ob-nunito')) { fontLoaded = true; return }
    const l = document.createElement('link')
    l.id = 'ob-nunito'; l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Nunito+Sans:opsz,wght@6..12,400;6..12,600;6..12,700;6..12,800&display=swap'
    document.head.appendChild(l)
    // Mockup hover states (.tile / .row from the prototype CSS).
    const st = document.createElement('style')
    st.id = 'ob-css'
    st.textContent = '.ob-tile:hover{border-color:#9DB8F2!important;box-shadow:0 4px 14px rgba(27,36,51,.08)}.ob-row:hover{background:#F7F9FC}'
    document.head.appendChild(st); fontLoaded = true
  }, [])
}

/* ── icons (no emojis in the module — Lee, 30 Oct 2026) ── */
export const LockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" />
  </svg>
)
export const ClockIcon: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
)
export const CheckCircleIcon: React.FC<{ size?: number; color?: string }> = ({ size = 13, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" />
  </svg>
)
export const WarnIcon: React.FC<{ size?: number; color?: string }> = ({ size = 12, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3L2.5 20h19L12 3z" /><path d="M12 10v4" /><path d="M12 17.5v.5" />
  </svg>
)

/* ── primitives ── */
type Div = React.HTMLAttributes<HTMLDivElement>

/** White card: 1px #E1E6EE border, 12px radius, 16/18 padding, column gap 10. */
export const Card: React.FC<Div & { pad?: string; gap?: number; row?: boolean }> = ({ pad = '16px 18px', gap = 10, row, style, children, ...rest }) => (
  <section style={{ background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 12, padding: pad, display: 'flex', flexDirection: row ? 'row' : 'column', gap, minWidth: 0, ...style }} {...rest}>{children}</section>
)

/** 11.5px/700 letter-spaced caps label. */
export const Eyebrow: React.FC<Div> = ({ style, children, ...rest }) => (
  <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', color: OB.faint, textTransform: 'uppercase', ...style }} {...rest}>{children}</span>
)

/** Rounded pill, 11.5px/800. */
export const Pill: React.FC<{ bg: string; fg: string; size?: number; style?: React.CSSProperties; children: React.ReactNode }> = ({ bg, fg, size = 11.5, style, children }) => (
  <span style={{ fontSize: size, fontWeight: 800, borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap', background: bg, color: fg, ...style }}>{children}</span>
)

const btnBase: React.CSSProperties = { font: 'inherit', cursor: 'pointer', boxSizing: 'border-box' }
/** Solid blue. */
export const BtnPrimary: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { small?: boolean }> = ({ small, style, children, ...rest }) => (
  <button style={{ ...btnBase, fontSize: small ? 13 : 14, fontWeight: 800, background: OB.accent, color: '#fff', border: 0, borderRadius: 10, padding: small ? '8px 14px' : '10px 16px', ...style }} {...rest}>{children}</button>
)
/** Soft blue chip-button. */
export const BtnSoft: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ style, children, ...rest }) => (
  <button style={{ ...btnBase, fontSize: 12.5, fontWeight: 700, background: OB.accentSoft, color: OB.accent, border: 0, borderRadius: 8, padding: '5px 10px', ...style }} {...rest}>{children}</button>
)
/** White outline. */
export const BtnOutline: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ style, children, ...rest }) => (
  <button style={{ ...btnBase, fontSize: 12.5, fontWeight: 700, background: OB.surface, color: OB.ink, border: `1px solid ${OB.border}`, borderRadius: 8, padding: '5px 10px', ...style }} {...rest}>{children}</button>
)

/** Round initials avatar (26px default). */
export const ObAvatar: React.FC<{ name?: string | null; size?: number; customer?: boolean }> = ({ name, size = 26, customer }) => (
  <span style={{ width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 800, color: '#fff', flex: `0 0 ${size}px`, background: personColor(name, customer) }}>
    {(name || '?').trim().slice(0, 2).toUpperCase()}
  </span>
)

/** Filter chip (round). Selected = solid blue; dark variant = #1B2433 fill. */
export const Chip: React.FC<{ on: boolean; dark?: boolean; onClick: () => void; children: React.ReactNode; style?: React.CSSProperties }> = ({ on, dark, onClick, children, style }) => (
  <button onClick={onClick} style={{ ...btnBase, fontSize: 13, borderRadius: 999, padding: '6px 14px', fontWeight: on ? 800 : 400, background: on ? (dark ? OB.ink : OB.accent) : OB.surface, color: on ? '#fff' : OB.ink, border: `1px solid ${on ? (dark ? OB.ink : OB.accent) : OB.border}`, ...style }}>{children}</button>
)

/** Stat tile (Board/Calendar/Analytics): 72px, toggles as a filter. */
export const StatTile: React.FC<{ label: string; value: React.ReactNode; on?: boolean; red?: boolean; onClick?: () => void }> = ({ label, value, on, red, onClick }) => (
  <button onClick={onClick} aria-pressed={!!on} style={{ ...btnBase, cursor: onClick ? 'pointer' : 'default', textAlign: 'left', background: on ? OB.accentPale : OB.surface, border: `1px solid ${on ? OB.accent : OB.border}`, borderRadius: 12, padding: '12px 14px', height: 72, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
    <span style={{ fontSize: 22, fontWeight: 800, color: red ? OB.red : OB.ink, lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color: OB.muted }}>{label}</span>
  </button>
)

/** Underline tab (role=tab style from the mockups). */
export const UTab: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode; count?: React.ReactNode }> = ({ on, onClick, children, count }) => (
  <button role="tab" onClick={onClick} style={{ ...btnBase, fontSize: 14, background: 'none', border: 0, padding: '10px 12px', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontWeight: on ? 800 : 400, color: on ? OB.accent : OB.muted, borderBottom: `3px solid ${on ? OB.accent : 'transparent'}` }}>
    {children}
    {count != null && <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '0 6px', background: on ? OB.accentSoft : OB.track, color: on ? OB.accent : OB.muted }}>{count}</span>}
  </button>
)

/** Mockup filter dropdown: "Name: value ▾" trigger + floating option panel.
 *  Single-select shows radio rings; multi shows check boxes + Clear/Done. */
export const DropMenu: React.FC<{
  name: string; value: string; active?: boolean
  options: Array<{ label: string; n?: number | string; on: boolean; pick: () => void }>
  multi?: boolean; onClear?: () => void
}> = ({ name, value, active, options, multi, onClear }) => {
  const [open, setOpen] = React.useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} aria-expanded={open}
        style={{ ...btnBase, fontSize: 13, background: OB.surface, border: `1px solid ${active ? OB.accent : OB.inputBorder}`, borderRadius: 8, padding: '8px 12px', minHeight: 38, display: 'flex', gap: 5, alignItems: 'center', color: OB.ink }}>
        <span style={{ color: OB.muted }}>{name}:</span> <strong>{value}</strong> <span aria-hidden>▾</span>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 42, left: 0, zIndex: 20, minWidth: 230, background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(27,36,51,.14)', padding: 6, display: 'flex', flexDirection: 'column' }}>
          {options.map((o) => (
            <button key={o.label} onClick={() => { o.pick(); if (!multi) setOpen(false) }}
              style={{ ...btnBase, fontSize: 13, textAlign: 'left', background: o.on ? '#F5F8FE' : OB.surface, border: 0, borderRadius: 6, padding: '7px 8px', display: 'flex', gap: 8, alignItems: 'center', color: OB.ink, fontWeight: o.on ? 800 : 400 }}>
              {multi
                ? <span style={{ width: 16, height: 16, borderRadius: 4, boxSizing: 'border-box', flex: '0 0 16px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, ...(o.on ? { background: OB.accent, color: '#fff' } : { border: `1.5px solid ${OB.faint2}`, color: 'transparent' }) }}>✓</span>
                : <span style={{ width: 14, height: 14, borderRadius: '50%', boxSizing: 'border-box', flex: '0 0 14px', border: o.on ? `4px solid ${OB.accent}` : `1.5px solid ${OB.faint2}` }} />}
              <span style={{ flex: 1 }}>{o.label}</span>
              {o.n != null && <span style={{ fontSize: 11.5, color: OB.faint }}>{o.n}</span>}
            </button>
          ))}
          {multi && (
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${OB.rowDiv}`, marginTop: 4, padding: '6px 6px 2px' }}>
              <button onClick={() => onClear?.()} style={{ ...btnBase, fontSize: 12.5, background: 'none', border: 0, color: OB.accent, fontWeight: 700 }}>Clear</button>
              <button onClick={() => setOpen(false)} style={{ ...btnBase, fontSize: 12.5, background: OB.accent, color: '#fff', border: 0, borderRadius: 6, padding: '4px 10px', fontWeight: 700 }}>Done</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Segmented view toggle (List | Grouped). */
export const SegToggle: React.FC<{ options: Array<[string, boolean, () => void]> }> = ({ options }) => (
  <div role="group" style={{ display: 'flex', background: OB.surface, border: `1px solid ${OB.border}`, borderRadius: 10, padding: 3 }}>
    {options.map(([l, on, pick]) => (
      <button key={l} onClick={pick} style={{ ...btnBase, fontSize: 13, fontWeight: 700, border: 0, borderRadius: 8, padding: '6px 14px', minHeight: 32, background: on ? OB.accent : 'transparent', color: on ? '#fff' : OB.ink2 }}>{l}</button>
    ))}
  </div>
)

export const fmtDay = (s?: string | null) => (s ? new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—')
export const fmtDayTime = (s?: string | null) => (s ? new Date(s).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—')
